import { ReactNode, useCallback, useEffect } from 'react';
import cn from 'classnames';
import { createPortal } from 'react-dom';
import { useUnmount } from 'react-use';

import styles from './Modal.module.scss';

interface IModalProps {
  visible?: boolean;
  modalClassName?: string;
  modalBodyClassName?: string;
  closeButtonClassName?: string;
  children: ReactNode;
  overflowBody?: 'unset';
  onClose?: () => void;
  afterClose?: () => void;
  isOutsideClick?: boolean;
}

const Modal = ({
  visible,
  modalClassName,
  modalBodyClassName,
  closeButtonClassName,
  children,
  onClose,
  afterClose,
  overflowBody,
  isOutsideClick,
}: IModalProps) => {
  const body = document.querySelector('body');
  const resetStyles = useCallback(() => {
    body?.removeAttribute('overflow');
  }, [body]);

  useEffect(() => {
    if (visible) {
      body?.setAttribute('overflow', 'hidden');
    } else {
      resetStyles();
    }
  }, [body, resetStyles, visible]);

  useUnmount(() => {
    resetStyles();
  });

  const handleClose = useCallback(() => {
    onClose?.();
    afterClose?.();
    resetStyles();
  }, [afterClose, onClose, resetStyles]);

  if (!visible || !body) {
    return null;
  }

  return createPortal(
    <div className={cn(styles.Modal, { [styles.ModalIsActive]: visible }, modalClassName)} id="modal">
      <div className={cn(styles.ModalBody, { [styles.BodyUnset]: overflowBody === 'unset' }, modalBodyClassName)}>
        {children}
        <button className={cn(styles.CloseButton, closeButtonClassName)} onClick={handleClose} type="button" />
      </div>
      <div className={styles.ModalBackground} onClick={isOutsideClick ? handleClose : undefined} />
    </div>,
    body,
  );
};

export { Modal };
